import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "../../../../styles/useCustomScrollbar";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  selectedDate?: string;
  children: React.ReactNode;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  selectedDate,
  children,
}) => {
  useCustomScrollbar();
  const [isAnimating, setIsAnimating] = useState(false);

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "Today";

    // Parse as local date to avoid timezone shifting
    const parts = dateStr.split("-").map(Number);
    const date = new Date(parts[0], parts[1] - 1, parts[2]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) return "Today";

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (compareDate.getTime() === yesterday.getTime()) return "Yesterday";

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      document.body.style.overflow = "unset";
      if (isAnimating) {
        const timer = setTimeout(() => setIsAnimating(false), 300);
        return () => clearTimeout(timer);
      }
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isAnimating]);

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div className="drawer-container">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-500 ease-in-out ${
          isOpen ? "bg-opacity-25" : "bg-opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        style={{
          zIndex: 9998,
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-out ${
          isOpen && isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          zIndex: 9999,
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "360px",
          maxWidth: "90vw",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-[16px] 3xl:text-[18px] font-semibold font-inter text-[#1E1F26]">
            {title}
          </h2>

          {/* Read-only date from TabNavigation */}
          <span className="text-[14px] text-[#003E3E] font-inter font-medium">
            {formatDate(selectedDate)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar-overlay">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Drawer;